// Vendors
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';

// Components
import globalLoader from '../../../site/global-loader';

// Utilities
import { bodyScroll } from '../../../lib/utilities';

const LOADING_STATE_NONE = 'NONE';
const LOADING_STATE_LOADING = 'LOADING';
const LOADING_STATE_LOADED = 'LOADED';
const LOADING_STATE_ERROR = 'ERROR';

const PageViewModal = () => {
  const defaultSettings = {
    size: 'full',
    width: null,
    hasPadding: true,
    verticalAlignment: 'center'
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [referrerTitle, setReferrerTitle] = useState('');
  const [isOpen, setOpen] = useState(false);
  const [loadingState, setLoadingState] = useState(LOADING_STATE_NONE);
  const [pageViewSrc, setPageViewSrc] = useState('');

  const hasCustomWidth = settings.size === 'custom' && settings.width;
  const customWidth = hasCustomWidth ? `${settings.width}px` : null;
  const htmlElement = document.documentElement;

  const modalClassName = classnames(
    'page-view-modal',
    'modal',
    'modal--no-close',
    'modal--full-height',
    `modal--${settings.size}`,
    `modal--valign-${settings.verticalAlignment}`,
    {
      'modal--visible': isOpen,
      [`modal--${settings.size}`]: !hasCustomWidth
    }
  );

  const modalContentClassName = classnames('modal__content', {
    'u--padding-0': !settings.hasPadding
  });

  const modalDialogStyle = {
    maxWidth: customWidth,
    flexBasis: customWidth
  };

  const ModalError = () => (
    <div className="notice notice--error">Modal not found.</div>
  );

  useEffect(() => {
    // Set listeners on load
    __setListeners();

    // Refresh listeners on facetWPUpdated
    document.addEventListener('facetWPUpdated', __setListeners);
  }, []);

  function __setListeners() {
    const modalTriggers = document.querySelectorAll('[data-page-view-modal]');

    modalTriggers.forEach((element) =>
      element.addEventListener('click', handleOpenClick)
    );
  }

  function openModal() {
    setOpen(true);
    bodyScroll.disable();
    htmlElement.classList.add('html--modal-visible');
  }

  function closeModal() {
    setOpen(false);
    setTimeout(() => {
      setPageViewSrc('');
      bodyScroll.enable();
      htmlElement.classList.remove('html--modal-visible');
      setLoadingState(LOADING_STATE_NONE);
    }, 300);
  }

  function handleOpenClick(event) {
    event.preventDefault();
    globalLoader.show();
    const { pageViewReferrerTitle } = event.currentTarget.dataset;
    setLoadingState(LOADING_STATE_LOADING);
    setPageViewSrc(event.currentTarget.href);
    setReferrerTitle(pageViewReferrerTitle);
  }

  function handleCloseClick(event) {
    event.preventDefault();
    closeModal();
  }

  function handleModalDialogClick(event) {
    event.stopPropagation();
  }

  function handleIframeLoad(event) {
    setLoadingState(LOADING_STATE_LOADED);
    globalLoader.hide();
    openModal();
  }

  return (
    <div
      className={modalClassName}
      aria-labelledby="page-view-modal-label"
      role="dialog"
      onClick={handleCloseClick}
    >
      <div
        className="modal__dialog"
        role="document"
        style={modalDialogStyle}
        onClick={handleModalDialogClick}
      >
        <div className={modalContentClassName}>
          <nav className="page-view-modal__nav">
            <div className="page-view-modal__nav__background-pattern" />
            <div className="page-view-modal__nav__content">
              <button
                className="btn btn--has-arrow-left btn--red module-hover-root"
                onClick={handleCloseClick}
              >
                <span className="btn__content btn--has-arrow-only">
                  <span className="btn__arrow arrow arrow--white arrow--left"></span>
                  <span className="btn__text u--display-none u--display-inline-sm-min">
                    Back{referrerTitle ? ` to ${referrerTitle}` : ''}
                  </span>
                  <span className="btn__text u--display-none-sm-min">Back</span>
                </span>
              </button>
              <a
                href={pageViewSrc}
                className="btn btn--link btn--white btn--has-underline"
                target="_blank"
              >
                <span className="btn__content u--padding-0-xs-max">
                  <span className="btn__text">
                    Open in a New Tab
                    <span className="btn__icon perc-icon-external-link"></span>
                  </span>
                </span>
              </a>
            </div>
          </nav>
          <section className="modal__body">
            {pageViewSrc && (
              <iframe
                name="pagePreview"
                src={`${pageViewSrc}?pagePreview=1`}
                onLoad={handleIframeLoad}
              />
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default PageViewModal;
